import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ACTION_LOG_LIMIT_MONTHS } from '../../lib/constants';
import * as action from '../../actions/actionLog';
import getOperators from '../../actions/api/operators/getOperators';
import { convertDateToISO8601, isGuest, showErrorToast } from '../../lib/tools';
import Calendar from '../Calendar';
import config from '../../config';
import messages from '../../lib/i18n';
import '../../styles/react-day-picker-style.css';
import AppButton from '../../styles/common/AppButton';
import MultipleSelectBox from '../Parts/MultipleSelectBox';
import ColorSF2 from '../../styles/common/ColorSF2';
import {
  SearchArea,
  SearchAreaWrapper,
  SearchOptionsTable,
  DateTimeTd,
  InputDatetimeWrapper,
  InputTitle,
  InputText,
  ButtonWrapper,
  TrFixedHeight,
} from './styles/ActionLogSearchBoxStyles';

const { locale } = config;
const i18nMessages = messages[locale];

const ActionLogSearchBox = ({
  dispatch,
  apiToken,
  searchOptions,
  page,
  groups,
  operators,
}) => {
  const currentUserRole = useSelector(state => state.main.innerRole);
  const isGuestUser = isGuest(currentUserRole);
  // 検索条件変更毎にアクションログ検索
  useEffect(() => {
    action.getActionLogs(dispatch, apiToken, { ...searchOptions, page });
  }, [dispatch, apiToken, searchOptions, page]);

  const intl = useIntl();
  const { formatMessage } = intl;
  const placeholderDevice = formatMessage({
    id: 'actionLog.searchBox.placeholder.device',
  });
  const { startDate, endDate } = searchOptions;
  /** 何ヶ月前か判定する */
  const checkMonthsAgo = inputDate => {
    const targetDate = new Date(inputDate);
    const sourceDate = new Date(endDate);
    const agoDate = new Date(sourceDate);
    agoDate.setMonth(sourceDate.getMonth() - ACTION_LOG_LIMIT_MONTHS);
    return sourceDate < targetDate || agoDate > targetDate;
  };
  /** 何ヶ月後か判定する */
  const checkMonthsLater = inputDate => {
    const targetDate = new Date(inputDate);
    const sourceDate = new Date(startDate);
    const laterDate = new Date(sourceDate);
    laterDate.setMonth(sourceDate.getMonth() + ACTION_LOG_LIMIT_MONTHS);
    laterDate.setDate(sourceDate.getDate() - 1);
    return sourceDate > targetDate || laterDate < targetDate;
  };
  // グループ検索用
  const groupNames = groups.map(params => params.name);
  // オペレーター検索用
  const operatorNames = operators.map(params => params.name);
  const operatorClearId = 'operatorClearButton';
  // アクション検索用
  const actions = i18nMessages['actionLog.list.actions'];
  const actionNames = Object.entries(actions).map(params => params[1]);
  /** ログ取得用 開始日時を更新する */
  const onUpdateStartDate = date => {
    dispatch({ type: action.ActionTypes.UPDATE_SEARCH_START_DATE, date });
  };
  /** ログ取得用 終了日時を更新する */
  const onUpdateEndDate = date => {
    dispatch({ type: action.ActionTypes.UPDATE_SEARCH_END_DATE, date });
  };
  /** ログ取得用 デバイス名称を更新する */
  const onUpdateDeviceName = deviceName => {
    dispatch({
      type: action.ActionTypes.UPDATE_SEARCH_DEVICE_NAME,
      deviceName,
    });
  };
  /** ログ取得用 アクションを更新する */
  const onUpdateAction = value => {
    dispatch({ type: action.ActionTypes.UPDATE_SEARCH_ACTION, action: value });
  };
  /** ログ取得用 グループを更新する */
  const onUpdateGroup = value => {
    dispatch({ type: action.ActionTypes.UPDATE_SEARCH_GROUP, groupId: value });
  };
  /** ログ取得用 オペレーターを更新する */
  const onUpdateOperatpr = value => {
    dispatch({
      type: action.ActionTypes.UPDATE_SEARCH_OPERATOR,
      operatorId: value,
    });
  };
  return (
    <SearchAreaWrapper id="ActionLogSearchBox">
      <SearchArea>
        <tbody>
          <tr>
            <td>
              <SearchOptionsTable>
                <tbody>
                  <TrFixedHeight>
                    <th>
                      <InputTitle>
                        <FormattedMessage id="actionLog.searchBox.title.date" />
                      </InputTitle>
                    </th>
                    <DateTimeTd>
                      <InputDatetimeWrapper>
                        <Calendar
                          value={startDate}
                          validation={checkMonthsAgo}
                          onSearch={date => {
                            if (date) {
                              onUpdateStartDate(convertDateToISO8601(date));
                            } else {
                              showErrorToast(
                                formatMessage({
                                  id: 'actionLog.searchBox.error.message.date',
                                }),
                              );
                            }
                          }}
                        />
                        <FormattedMessage id="actionLog.searchBox.fullTilde" />
                        <Calendar
                          value={endDate}
                          validation={checkMonthsLater}
                          onSearch={date => {
                            if (date) {
                              onUpdateEndDate(convertDateToISO8601(date));
                            } else {
                              showErrorToast(
                                formatMessage({
                                  id: 'actionLog.searchBox.error.message.date',
                                }),
                              );
                            }
                          }}
                        />
                      </InputDatetimeWrapper>
                    </DateTimeTd>
                    {groups && (
                      <>
                        <th>
                          <InputTitle>
                            <FormattedMessage id="actionLog.searchBox.title.group" />
                          </InputTitle>
                        </th>
                        <td>
                          <MultipleSelectBox
                            list={groupNames}
                            onSelect={ids => {
                              const selectedGroups =
                                action.convertSelectedGroupList(groups, ids);
                              onUpdateGroup(selectedGroups.join(' '));
                              dispatch(
                                getOperators(
                                  apiToken,
                                  { groupIds: selectedGroups },
                                  action.ActionTypes.SUCCESS_API_GET_OPERATORS,
                                ),
                              );
                              // オペレーターセレクトボックス クリアボタンイベント発火
                              const clearButton =
                                document.getElementById(operatorClearId);
                              if (clearButton) {
                                clearButton.click();
                              }
                            }}
                          />
                        </td>
                      </>
                    )}
                    {!isGuestUser && (
                      <>
                        <th>
                          <InputTitle>
                            <FormattedMessage id="actionLog.searchBox.title.operator" />
                          </InputTitle>
                        </th>
                        <td>
                          <MultipleSelectBox
                            list={operatorNames}
                            onSelect={ids => {
                              const selectedOperators =
                                action.convertSelectedOperatorList(
                                  operators,
                                  ids,
                                );
                              onUpdateOperatpr(selectedOperators.join(' '));
                            }}
                            clearId={operatorClearId}
                          />
                        </td>
                      </>
                    )}
                  </TrFixedHeight>
                  <TrFixedHeight>
                    <th>
                      <InputTitle>
                        <FormattedMessage id="actionLog.searchBox.title.action" />
                      </InputTitle>
                    </th>
                    <td>
                      <MultipleSelectBox
                        list={actionNames}
                        onSelect={ids => {
                          const selectedActions =
                            action.convertSelectedActionList(actions, ids);
                          onUpdateAction(selectedActions.join(' '));
                        }}
                      />
                    </td>
                    <th>
                      <FormattedMessage id="actionLog.searchBox.title.device" />
                    </th>
                    <td colSpan="3">
                      <InputText
                        id="actionLogSearchBoxPartialDeviceNickname"
                        type="text"
                        placeholder={placeholderDevice}
                        onInput={e => onUpdateDeviceName(e.target.value)}
                      />
                    </td>
                  </TrFixedHeight>
                </tbody>
              </SearchOptionsTable>
            </td>
            <td>
              <ButtonWrapper>
                <AppButton
                  small
                  width="120px"
                  className="button"
                  id="searchTimelineTag"
                  role="presentation"
                  bgColor={ColorSF2.white}
                  color="black"
                  borderColor={ColorSF2.mediumGray}
                  onClick={() => {
                    action.downloadLogsCsv(dispatch, apiToken, searchOptions);
                  }}
                >
                  <FormattedMessage id="actionLog.button.csvExport" />
                </AppButton>
              </ButtonWrapper>
            </td>
          </tr>
        </tbody>
      </SearchArea>
    </SearchAreaWrapper>
  );
};

ActionLogSearchBox.propTypes = {
  dispatch: PropTypes.func.isRequired,
  apiToken: PropTypes.string.isRequired,
  searchOptions: PropTypes.object.isRequired,
  page: PropTypes.number.isRequired,
  groups: PropTypes.array.isRequired,
  operators: PropTypes.array.isRequired,
};

export default ActionLogSearchBox;
